import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBits";
import { api, errMsg, fmtDT, fmtDur, label } from "@/lib/api";

const Info = ({ k, v }) => (
  <div className="min-w-0">
    <dt className="text-[11px] uppercase tracking-wide text-slate-400">{k}</dt>
    <dd className="text-sm text-slate-800 truncate">{v || "—"}</dd>
  </div>
);

export const Transcript = ({ items }) => (
  <div className="space-y-1.5 max-h-72 overflow-y-auto scroll-thin" data-testid="transcript">
    {(items || []).map((t, i) => (
      <div key={i} className={`flex ${t.role === "client" ? "justify-start" : "justify-end"}`}>
        <div className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs ${t.role === "client" ? "bg-slate-100 text-slate-800" : t.role === "system" ? "bg-blue-50 text-blue-800" : "bg-zinc-900 text-white"}`}>
          <span className="block text-[10px] opacity-60">{t.role === "client" ? "Contact" : t.role === "ai" ? "AI" : "System"}</span>{t.content}
        </div>
      </div>
    ))}
  </div>
);

const CallRow = ({ c }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-md" data-testid={`contact-call-${c.id}`}>
      <button className="w-full flex items-center gap-2 px-3 py-2 text-left" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <span className="text-xs text-slate-500 w-36 shrink-0">{fmtDT(c.created_at)}</span>
        <span className="text-xs text-slate-700 flex-1 truncate">{label(c.mode)} · {c.direction} · {c.from_number || ""} · {c.agent_name || ""}</span>
        <span className="text-xs text-slate-500">{fmtDur(c.duration_seconds)}</span>
        <StatusBadge value={c.outcome || c.status} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 text-xs text-slate-600">
          {c.summary && <p data-testid="call-summary"><b>Summary:</b> {c.summary}</p>}
          {c.notes && <p><b>Notes:</b> {c.notes}</p>}
          {c.script_name && <p><b>Script:</b> {c.script_name}</p>}
          {c.twilio_sid && <p className="font-mono text-[10px]">Provider ID: {c.twilio_sid}</p>}
          {c.transcript?.length > 0 && <Transcript items={c.transcript} />}
        </div>
      )}
    </div>
  );
};

const Sms = ({ contact, reload }) => {
  const [text, setText] = useState("");
  const send = async () => {
    try {
      await api.post(`/contacts/${contact.id}/sms`, { body: text });
      setText("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const blocked = contact.sms_opt_out || ["opted_out", "do_not_call", "wrong_number"].includes(contact.status);
  return (
    <div className="space-y-2">
      <div className="space-y-1.5 max-h-60 overflow-y-auto scroll-thin">
        {contact.messages.map((m) => (
          <div key={m.id} className={`flex ${m.direction === "inbound" ? "justify-start" : "justify-end"}`}>
            <div className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-xs ${m.direction === "inbound" ? "bg-slate-100" : "bg-blue-600 text-white"}`}>
              {m.body}<span className="block text-[10px] opacity-60">{fmtDT(m.created_at)}{m.auto ? " · automatic" : ""}</span>
            </div>
          </div>
        ))}
        {!contact.messages.length && <p className="text-xs text-slate-500">No messages yet.</p>}
      </div>
      {contact.sms_paused && (
        <p className="text-xs text-amber-700">Automatic texts paused after the contact replied. <button className="underline" data-testid="sms-resume-auto-btn" onClick={() => api.post(`/contacts/${contact.id}/sms/resume-auto`).then(reload)}>Resume</button></p>
      )}
      {blocked ? <p className="text-xs text-rose-700" data-testid="sms-blocked">Texting disabled — contact opted out / do not contact.</p> : (
        <div className="flex gap-2">
          <Textarea rows={2} data-testid="sms-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Manual reply" />
          <Button size="sm" data-testid="sms-send-btn" disabled={!text.trim()} onClick={send}><MessageSquare className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
};

const NextActions = ({ onAction }) => {
  const [cb, setCb] = useState("");
  return (
    <div className="mt-2 pt-2 border-t border-amber-200 space-y-2" data-testid="next-actions">
      <p className="text-[11px]">You already spoke with this person, so the queue won't repeat-call them automatically. Pressing Call still dials right away. Or choose:</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" className="h-7 text-xs" data-testid="next-call-again-btn" onClick={() => onAction("call_again")}>Call again</Button>
        <Input type="datetime-local" className="h-7 w-48 text-xs bg-white" data-testid="next-callback-input" value={cb} onChange={(e) => setCb(e.target.value)} />
        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="next-callback-btn" disabled={!cb} onClick={() => onAction("callback", { callback_at: new Date(cb).toISOString() })}>Schedule callback</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="next-resolve-btn" onClick={() => onAction("resolve")}>Mark resolved</Button>
      </div>
    </div>
  );
};

export const ContactPanel = ({ contact, reload }) => {
  const [note, setNote] = useState("");
  if (!contact) return <p className="text-sm text-slate-500" data-testid="no-contact-selected">Select a contact from the queue.</p>;
  const addNote = async () => {
    try {
      await api.post(`/contacts/${contact.id}/notes`, { text: note });
      setNote("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const el = contact.eligibility;
  const nextAction = async (action, extra = {}) => {
    try {
      await api.post(`/contacts/${contact.id}/next-action`, { action, ...extra });
      toast.success(action === "call_again" ? "Ready to call again" : action === "callback" ? "Callback scheduled" : "Marked resolved");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const finished = contact.status === "contacted" && !contact.callback_at;
  return (
    <div className="space-y-4" data-testid="contact-panel">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-heading text-xl font-semibold tracking-tight text-slate-900" data-testid="contact-name">{contact.name}</h2>
          <p className="text-sm text-slate-500">{contact.business} · <span className="font-mono">{contact.phone}</span></p>
        </div>
        <StatusBadge value={contact.status} testId="contact-status" />
      </div>
      <div className={`rounded-md border px-3 py-2 text-xs ${el.manual.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="contact-eligibility">
        <b>{el.manual.ok ? "Eligible to call now" : el.manual.reason}</b>
        {el.manual.next_eligible_at && <> · next eligible {fmtDT(el.manual.next_eligible_at)}</>}
        {el.manual.ok && !el.automatic.ok && <> · automatic retry: {el.automatic.reason} {el.automatic.next_eligible_at ? fmtDT(el.automatic.next_eligible_at) : ""}</>}
        <span className="block text-[11px] opacity-80">Attempts: {contact.attempts_total || 0} · last {fmtDT(contact.last_attempt_at)} · callback {fmtDT(contact.callback_at)}</span>
        {finished && <NextActions onAction={nextAction} />}
        {/Cooldown|Daily attempt|Outside calling hours|Sequence/.test(el.manual.reason) && (
          <span className="block text-[11px] mt-1" data-testid="rules-hint">This limit comes from Settings → Calling rules (admins can change it).</span>
        )}
      </div>
      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes ({contact.notes?.length || 0})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Calls ({contact.calls.length})</TabsTrigger>
          <TabsTrigger value="sms" data-testid="tab-sms">Texts</TabsTrigger>
        </TabsList>
        <TabsContent value="details">
          <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
            <Info k="Email" v={contact.email} /><Info k="Timezone" v={contact.timezone || "Default"} /><Info k="Tags" v={(contact.tags || []).join(", ")} />
            <Info k="Amount owed" v={contact.amount_owed} /><Info k="Approval amount" v={contact.approval_amount} /><Info k="Lender" v={contact.lender} />
            <Info k="Terms" v={contact.terms} /><Info k="Missing documents" v={contact.missing_documents} /><Info k="Offer details" v={contact.offer_details} />
            <Info k="SMS permission" v={contact.sms_consent ? "Yes" : "No"} /><Info k="Last outcome" v={label(contact.last_outcome)} />
          </dl>
        </TabsContent>
        <TabsContent value="notes" className="space-y-2">
          <div className="flex gap-2">
            <Textarea rows={2} data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note" />
            <Button size="sm" data-testid="note-add-btn" disabled={!note.trim()} onClick={addNote}>Add</Button>
          </div>
          {[...(contact.notes || [])].reverse().map((n) => (
            <div key={n.id} className="text-sm border-l-2 border-slate-200 pl-3"><p className="text-slate-800">{n.text}</p><p className="text-[11px] text-slate-400">{n.by} · {fmtDT(n.at)}</p></div>
          ))}
        </TabsContent>
        <TabsContent value="history" className="space-y-2">
          {contact.calls.map((c) => <CallRow key={c.id} c={c} />)}
          {!contact.calls.length && <p className="text-xs text-slate-500">No calls yet.</p>}
        </TabsContent>
        <TabsContent value="sms"><Sms contact={contact} reload={reload} /></TabsContent>
      </Tabs>
    </div>
  );
};
